<script setup lang="ts">
import { XCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIModal, UIModalContent, UIModalFooter, UIModalHeader } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

/**
 * Props interface for the remove primary field modal
 */
interface Props {
  show: boolean;
  fieldName: string;
  objectName: string;
  objectSingular: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:cancel": [value: boolean];
  "update:removeAll": [value: boolean];
}>();

/**
 * Normalize objectSingular for company object: use "Company" instead of "Company Field(s)"
 */
const displayObjectSingular = computed(() => {
  const singular = props.objectSingular || "";
  const normalized = singular.trim().toLowerCase();
  if (normalized === "company field" || normalized === "company fields") {
    return "Company";
  }
  return singular;
});

/**
 * Supporting text with dynamic field and object names
 */
const supportingText = computed(() => {
  return t("common.primaryFieldWarning", {
    fieldName: props.fieldName,
    objectSingular: displayObjectSingular.value,
  });
});

/**
 * Dynamic title for the modal header
 */
const modalTitle = computed(() => {
  return t("common.primaryFieldTitle", {
    fieldName: props.fieldName,
    objectSingular: displayObjectSingular.value,
  });
});

/**
 * Dynamic button text for remove all action
 */
const removeAllButtonText = computed(() => {
  return t("common.removeAllFields", {
    objectSingular: displayObjectSingular.value,
  });
});

/**
 * Handle cancel action
 */
const handleCancel = () => {
  emit("update:cancel", false);
};

/**
 * Handle remove all action
 */
const handleRemoveAll = () => {
  emit("update:removeAll", true);
};
</script>

<template>
  <UIModal :show="show" @update:show="handleCancel">
    <template #header>
      <UIModalHeader
        id="remove-primary-field-modal-header"
        :title="modalTitle"
        :icon="XCircleIcon"
        type="error"
        @close="handleCancel"
      />
    </template>

    <UIModalContent>
      <div class="text-sm text-gray-600">
        {{ supportingText }}
      </div>
    </UIModalContent>

    <template #footer>
      <UIModalFooter
        id="remove-primary-field-modal-footer"
        :positive-text="t('common.keepThisField')"
        :negative-text="removeAllButtonText"
        negative-type="secondary"
        @positive-click="handleCancel"
        @negative-click="handleRemoveAll"
      />
    </template>
  </UIModal>
</template>
